RegExp.escape = function (s) {
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

export class CSV {
  private static defaults: any = {
    separator: ',',
    delimiter: '"',
    headers: true
  };

  public static toArray(csv: string, options: any = {}): any[] {
    const config: any = {};
    config.separator = 'separator' in options ? options.separator : this.defaults.separator;
    config.delimiter = 'delimiter' in options ? options.delimiter : this.defaults.delimiter;
    const state = (options.state !== undefined ? options.state : {});

    options = {
      delimiter: config.delimiter,
      separator: config.separator,
      state
    };

    return this.parseEntry(csv, options);
  }

  public static toArrays(csv: string, options: any = {}) {
    const config: any = {};
    config.separator = 'separator' in options ? options.separator : this.defaults.separator;
    config.delimiter = 'delimiter' in options ? options.delimiter : this.defaults.delimiter;

    options = {
      delimiter: config.delimiter,
      separator: config.separator,
      start: options.start,
      end: options.end,
      state: {
        rowNum: 1,
        colNum: 1
      }
    };

    return this.parse(csv, options);
  }

  public static fromArrays(arrays: Array<any[]>, options: any = {}): string {
    const config: any = {};
    config.separator = 'separator' in options ? options.separator : this.defaults.separator;
    config.delimiter = 'delimiter' in options ? options.delimiter : this.defaults.delimiter;

    let output: string = '';

    for (let i = 0; i < arrays.length; i++) {
      const line: any[] = arrays[i];
      const lineValues: any[] = [];
      for (let j = 0; j < line.length; j++) {
        let strValue: string = (line[j] === undefined || line[j] === null) ? '' : line[j].toString();
        if (strValue.indexOf(config.delimiter) > -1) {
          strValue = strValue.replace(new RegExp(config.delimiter, 'g'), config.delimiter + config.delimiter);
        }

        let escMatcher: string = '\n|\r|S|D';
        escMatcher = escMatcher.replace('S', config.separator);
        escMatcher = escMatcher.replace('D', config.delimiter);

        if (strValue.search(escMatcher) > -1) {
          strValue = config.delimiter + strValue + config.delimiter;
        }
        lineValues.push(strValue);
      }
      output += lineValues.join(config.separator) + '\n';
    }

    return output;
  }

  private static parse(csv: string, options: any = {}): any[] {
    // cache settings
    const separator: string = options.separator;
    const delimiter: string = options.delimiter;

    // set initial state if it's missing
    if (!options.state.rowNum) {
      options.state.rowNum = 1;
    }
    if (!options.state.colNum) {
      options.state.colNum = 1;
    }

    // clear initial state
    const data: any[] = [];
    let entry: any[] = [];
    let state: number = 0;
    let value: string = '';
    let exit: boolean = false;

    function endOfEntry(): void {
      // reset the state
      state = 0;
      value = '';

      // if 'start' hasn't been met, don't output
      if (options.start && options.state.rowNum < options.start) {
        // update global state
        entry = [];
        options.state.rowNum++;
        options.state.colNum = 1;
        return;
      }

      data.push(entry);

      // cleanup
      entry = [];

      // if 'end' is met, stop parsing
      if (options.end && options.state.rowNum >= options.end) {
        exit = true;
      }

      // update global state
      options.state.rowNum++;
      options.state.colNum = 1;
    }

    function endOfValue(): void {
      entry.push(value);
      // reset the state
      value = '';
      state = 0;
      // update global state
      options.state.colNum++;
    }

    // escape regex-specific control chars
    const escSeparator: string = RegExp.escape(separator);
    const escDelimiter: string = RegExp.escape(delimiter);

    // compile the regEx str using the custom delimiter/separator
    let match: RegExp = /(D|S|\r\n|\n|\r|[^DS\r\n]+)/;
    let matchSrc: string = match.source;
    matchSrc = matchSrc.replace(/S/g, escSeparator);
    matchSrc = matchSrc.replace(/D/g, escDelimiter);
    match = new RegExp(matchSrc, 'gm');

    csv.replace(match, (m0: string): string => {
      if (exit) {
        return;
      }
      switch (state) {
        // the start of a value
        case 0:
          // null last value
          if (m0 === separator) {
            value += '';
            endOfValue();
            break;
          }
          // opening delimiter
          if (m0 === delimiter) {
            state = 1;
            break;
          }
          // null last value
          if (/^(\r\n|\n|\r)$/.test(m0)) {
            endOfValue();
            endOfEntry();
            break;
          }
          // un-delimited value
          value += m0;
          state = 3;
          break;

        // delimited input
        case 1:
          // second delimiter? check further
          if (m0 === delimiter) {
            state = 2;
            break;
          }
          // delimited data
          value += m0;
          state = 1;
          break;

        // delimiter found in delimited input
        case 2:
          // escaped delimiter?
          if (m0 === delimiter) {
            value += m0;
            state = 1;
            break;
          }
          // null value
          if (m0 === separator) {
            endOfValue();
            break;
          }
          // end of entry
          if (/^(\r\n|\n|\r)$/.test(m0)) {
            endOfValue();
            endOfEntry();
            break;
          }
          // broken paser?
          throw new Error(`CSVDataError: Illegal State [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);

        // un-delimited input
        case 3:
          // null last value
          if (m0 === separator) {
            endOfValue();
            break;
          }
          // end of entry
          if (/^(\r\n|\n|\r)$/.test(m0)) {
            endOfValue();
            endOfEntry();
            break;
          }
          if (m0 === delimiter) {
            // non-compliant data
            throw new Error(`CSVDataError: Illegal Quote [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);
          }
          // broken parser?
          throw new Error(`CSVDataError: Illegal Data [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);
        default:
          // shenanigans
          throw new Error(`CSVDataError: Unknown State [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);
      }
    });

    // submit the last entry
    // ignore null last line
    if (entry.length !== 0) {
      endOfValue();
      endOfEntry();
    }

    return data;
  }

  // a csv entry parser
  private static parseEntry(csv: string, options: any = {}): any[] {
    const separator: string = options.separator;
    const delimiter: string = options.delimiter;

    // set initial state if it's missing
    if (!options.state.rowNum) {
      options.state.rowNum = 1;
    }
    if (!options.state.colNum) {
      options.state.colNum = 1;
    }

    // clear initial state
    const entry: any[] = [];
    let state: number = 0;
    let value: string = '';

    function endOfValue(): void {
      entry.push(value);
      // reset the state
      value = '';
      state = 0;
      // update global state
      options.state.colNum++;
    }

    // checked for a cached regEx first
    if (!options.match) {
      // escape regex-specific control chars
      const escSeparator: string = RegExp.escape(separator);
      const escDelimiter: string = RegExp.escape(delimiter);

      // compile the regEx str using the custom delimiter/separator
      const match: RegExp = /(D|S|\n|\r|[^DS\r\n]+)/;
      let matchSrc: string = match.source;
      matchSrc = matchSrc.replace(/S/g, escSeparator);
      matchSrc = matchSrc.replace(/D/g, escDelimiter);
      options.match = new RegExp(matchSrc, 'gm');
    }

    // put on your fancy pants...
    // process control chars individually, use look-ahead on non-control chars
    csv.replace(options.match, (m0: string): any => {
      switch (state) {
        // the start of a value
        case 0:
          // null last value
          if (m0 === separator) {
            value += '';
            endOfValue();
            break;
          }
          // opening delimiter
          if (m0 === delimiter) {
            state = 1;
            break;
          }
          // skip un-delimited new-lines
          if (m0 === '\n' || m0 === '\r') {
            break;
          }
          // un-delimited value
          value += m0;
          state = 3;
          break;

        // delimited input
        case 1:
          // second delimiter? check further
          if (m0 === delimiter) {
            state = 2;
            break;
          }
          // delimited data
          value += m0;
          state = 1;
          break;

        // delimiter found in delimited input
        case 2:
          // escaped delimiter?
          if (m0 === delimiter) {
            value += m0;
            state = 1;
            break;
          }
          // null value
          if (m0 === separator) {
            endOfValue();
            break;
          }
          // skip un-delimited new-lines
          if (m0 === '\n' || m0 === '\r') {
            break;
          }
          // broken paser?
          throw new Error(`CSVDataError: Illegal State [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);

        // un-delimited input
        case 3:
          // null last value
          if (m0 === separator) {
            endOfValue();
            break;
          }
          // skip un-delimited new-lines
          if (m0 === '\n' || m0 === '\r') {
            break;
          }
          // non-compliant data
          if (m0 === delimiter) {
            throw new Error(`CSVDataError: Illegal Quote [Row: ${options.state.rowNum}][Col:${options.state.colNum}]`);
          }
          // broken parser?
          throw new Error(`CSVDataError: Illegal Data [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);
        default:
          // shenanigans
          throw new Error(`CSVDataError: Unknown State [Row:${options.state.rowNum}][Col:${options.state.colNum}]`);
      }
    });

    // submit the last value
    endOfValue();

    return entry;
  }
}
